import { Button } from "@capacit-com/lara-law-shared-ui";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from "@capacit-com/lara-law-shared-ui";
import { Checkbox } from "@capacit-com/lara-law-shared-ui";
import { Input } from "@capacit-com/lara-law-shared-ui";
import {
  ChevronDown,
  FileCode,
  FilterIcon,
  FolderPlus,
  FolderTree,
  FunnelXIcon,
  Grid,
  Link2,
  List,
  Plus,
  RefreshCw,
  Search,
  Upload,
} from "lucide-react";
import OneDriveIcon from "@/assets/file-icons/onedrive.svg";
import MSIcon from "@/assets/3rd-party/ms_logo.svg";
import { useEffect, useRef, useState } from "react";
import { useSearchFromNavigation } from "@/pages/workspace/hooks/useSearchFromNavigation";
import {
  LinkedMsalAccount,
  logoutLinkedAccount,
  onMsalAccountChange,
} from "@/services/microsoft/auth";
import { useWorkspaceContext } from "@/pages/workspace/providers/WorkspaceContext";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";
import { useSharePointContext } from "@/pages/workspace/providers/SharePointContext";
import { useAfterMenuClose } from "@/pages/workspace/hooks/useAfterMenuClose";
import { useUploadHub } from "@/pages/workspace/hooks/useUploadHub";
import useFileSync from "@/pages/workspace/hooks/useFileSync";
import { METADATA_SORT_PREFIX } from "@/pages/workspace/utils/workspaceItemUtils";

const SORT_BY_LABELS: Record<string, string> = {
  name: "Navn",
  date: "Ændret",
  size: "Størrelse",
  isIndexed: "Status",
};

const getSortByLabel = (value: string, metadataOptions: string[]): string => {
  if (SORT_BY_LABELS[value]) return SORT_BY_LABELS[value];
  if (value.startsWith(METADATA_SORT_PREFIX)) {
    const name = value.slice(METADATA_SORT_PREFIX.length);
    return metadataOptions.includes(name) ? name : value;
  }
  return value;
};

type FilesTabToolbarProps = {
  sortBy: string;
  setSortBy: (sortBy: string) => void;
  viewType: "list" | "grid";
  setViewType: (viewType: "list" | "grid") => void;
  setIsCreateFolderDialogOpen: (open: boolean) => void;
  setIsAddLinkDialogOpen: (open: boolean) => void;
};

const FilesTabToolbar = ({
  sortBy,
  setSortBy,
  viewType,
  setViewType,
  setIsCreateFolderDialogOpen,
  setIsAddLinkDialogOpen,
}: FilesTabToolbarProps) => {
  const { onUploadFilesFromComputer, onUploadFolderFromComputer } =
    useUploadHub();

  const [acct, setAcct] = useState<LinkedMsalAccount | null>(null);
  const [tooltipVisible, setTooltipVisible] = useState(false);

  useEffect(() => {
    // keep state in sync with MSAL events
    const off = onMsalAccountChange(setAcct);
    return off;
  }, []);

  const {
    search,
    setSearch,
    metadataOptions,
    folderOptions,
    selectedMetadataFilters,
    setSelectedMetadataFilters,
    canUserUploadFiles,
  } = useWorkspaceContext();
  const searchInputRef = useRef<HTMLInputElement>(null);
  useSearchFromNavigation(search.searchString, setSearch, searchInputRef);
  const { openCloudLocationChooser } = useSharePointContext();
  const addMenuClose = useAfterMenuClose();
  const { handleSyncAll, documentsSynchronizing } = useFileSync();

  const toggleMetadata = (metadataName: string) => {
    setSelectedMetadataFilters((prev) => ({
      ...prev,
      metadataNames: prev.metadataNames.includes(metadataName)
        ? prev.metadataNames.filter((name) => name !== metadataName)
        : [...prev.metadataNames, metadataName],
    }));
  };

  const toggleFolder = (folder: { name: string; id: string }) => {
    if (folder.name === "@onlyCurrentFolder") {
      setSearch((prev) => {
        // If @onlyCurrentFolder is already selected, unselect it (clear all folder selections)
        if (prev.folders.some((f) => f.name === "@onlyCurrentFolder")) {
          return {
            ...prev,
            folders: [],
          };
        }
        // Otherwise, select @onlyCurrentFolder and unselect all other folders
        return {
          ...prev,
          folders: [folder],
        };
      });
    } else {
      setSearch((prev) => {
        // If @onlyCurrentFolder is currently selected and we're selecting another folder,
        // remove @onlyCurrentFolder and add the new folder
        if (prev.folders.some((f) => f.name === "@onlyCurrentFolder")) {
          return {
            ...prev,
            folders: [folder],
          };
        }

        // Normal toggle behavior for other folders
        return {
          ...prev,
          folders: prev.folders.some(
            (f: { id: string; name: string }) => f.id === folder.id
          )
            ? prev.folders.filter(
                (f: { id: string; name: string }) => f.id !== folder.id
              )
            : [...prev.folders, folder],
        };
      });
    }
  };

  const clearAllFilters = () => {
    setSelectedMetadataFilters({ metadataNames: [] });
  };

  const hasActiveFilters = selectedMetadataFilters.metadataNames.length > 0;
  return (
    <>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder={`Søg ${
              search.folders.length > 0 &&
              !search.folders.some((f) => f.name === "@onlyCurrentFolder")
                ? "i valgte mapper"
                : search.folders.some((f) => f.name === "@onlyCurrentFolder")
                  ? "efter filer i nuværende mappe"
                  : "i hele workspacet"
            }...`}
            className="pl-8"
            value={search.searchString}
            onChange={(e) =>
              setSearch((prev) => ({ ...prev, searchString: e.target.value }))
            }
          />
          <DropdownMenu>
            <DropdownMenuTrigger asChild className="relative">
              <Button
                variant="ghost"
                size="icon"
                className={`absolute right-2.5 top-1/2 -translate-y-1/2 p-0 size-7 `}
              >
                <FilterIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80">
              <DropdownMenuItem
                disabled={
                  search.folders.length === 0 && !search.searchOnMetadata
                }
                className="text-destructive cursor-pointer hover:text-destructive group"
                onClick={(e) => {
                  e.preventDefault();
                  setSearch((prev) => ({
                    ...prev,
                    folders: [],
                    searchOnMetadata: false,
                  }));
                }}
              >
                <FunnelXIcon className="size-4 text-destructive group-hover:text-foreground" />
                Nulstil filter
              </DropdownMenuItem>

              <DropdownMenuItem
                key={`search-on-metadata`}
                onSelect={(e) => {
                  e.preventDefault();
                  setSearch((prev) => ({
                    searchString: prev.searchString,
                    folders: prev.folders,
                    searchOnMetadata: !prev.searchOnMetadata,
                  }));
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={search.searchOnMetadata}
                  className="[&_svg]:text-primary-foreground! cursor-pointer"
                />
                Inkluder metadata i søgning
              </DropdownMenuItem>

              <DropdownMenuItem
                key={`folder-@onlyCurrentFolder`}
                onClick={(e) => {
                  e.preventDefault();
                  toggleFolder({
                    name: "@onlyCurrentFolder",
                    id: "CurrentFolderId",
                  });
                }}
                className="flex items-center gap-2 cursor-pointer"
              >
                <Checkbox
                  checked={search.folders.some(
                    (f) => f.name === "@onlyCurrentFolder"
                  )}
                  className="[&_svg]:text-primary-foreground! cursor-pointer"
                />
                Søg kun i nuværende mappe
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <div className="max-h-60 overflow-y-auto">
                <DropdownMenuLabel className="text-xs text-muted-foreground py-1 sticky top-0 bg-background z-10">
                  Søg i følgende mapper
                </DropdownMenuLabel>
                {folderOptions.map((folder) => (
                  <DropdownMenuItem
                    key={`folder-${folder.name}-${folder.id}`}
                    onClick={(e) => {
                      e.preventDefault();
                      toggleFolder(folder);
                    }}
                    className="flex items-center gap-2 cursor-pointer"
                  >
                    <Checkbox
                      checked={search.folders.some((f) => f.id === folder.id)}
                      className="[&_svg]:text-primary-foreground! cursor-pointer"
                    />
                    {folder.name}
                  </DropdownMenuItem>
                ))}
              </div>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <DropdownMenu>
          <Tooltip>
            <TooltipTrigger
              asChild
              onPointerEnter={() => setTooltipVisible(true)}
              onPointerLeave={() => setTooltipVisible(false)}
            >
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={cn(
                    hasActiveFilters &&
                      "bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground",
                    metadataOptions.length === 0 &&
                      "opacity-50 cursor-not-allowed"
                  )}
                >
                  <FileCode className="size-4" />
                </Button>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            {tooltipVisible && (
              <TooltipContent>
                {metadataOptions.length > 0
                  ? "Denne filtrering vil kun vise filer med det valgte metadata."
                  : "Ingen dokumenter med metadata"}
              </TooltipContent>
            )}
          </Tooltip>

          {metadataOptions.length > 0 && (
            <DropdownMenuContent
              onCloseAutoFocus={() => setTooltipVisible(false)}
            >
              <DropdownMenuItem
                onClick={clearAllFilters}
                disabled={!hasActiveFilters}
                className="text-destructive cursor-pointer py-1 hover:text-destructive"
              >
                Ryd metadata filtre
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {metadataOptions.length > 0 && (
                <>
                  <DropdownMenuLabel className="text-xs text-muted-foreground py-1 flex items-center gap-2">
                    Filtrer på metadata
                  </DropdownMenuLabel>
                  {metadataOptions.map((metadataName) => (
                    <DropdownMenuItem
                      key={`metadata-${metadataName}`}
                      onClick={(e) => {
                        e.preventDefault();
                        toggleMetadata(metadataName);
                      }}
                      className="flex items-center gap-2 cursor-pointer"
                    >
                      <Checkbox
                        checked={selectedMetadataFilters.metadataNames.includes(
                          metadataName
                        )}
                        className="[&_svg]:text-primary-foreground! cursor-pointer"
                      />
                      {metadataName}
                    </DropdownMenuItem>
                  ))}
                </>
              )}
            </DropdownMenuContent>
          )}
        </DropdownMenu>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="w-[130px] justify-between"
              id="workspace-sort-by-trigger"
            >
              {getSortByLabel(sortBy, metadataOptions)}
              <ChevronDown className="ml-2 size-4 opacity-50" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-[200px]">
            <DropdownMenuLabel className="text-xs text-muted-foreground py-1">
              Sorter efter
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={() => setSortBy("name")}
              className="cursor-pointer"
            >
              Navn
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setSortBy("date")}
              className="cursor-pointer"
            >
              Ændret
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setSortBy("size")}
              className="cursor-pointer"
            >
              Størrelse
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => setSortBy("isIndexed")}
              className="cursor-pointer"
            >
              Status
            </DropdownMenuItem>
            {metadataOptions.length > 0 && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuLabel className="text-xs text-muted-foreground py-1">
                  Sorter efter metadata
                </DropdownMenuLabel>
                {metadataOptions.map((metadataName) => (
                  <DropdownMenuItem
                    key={`sort-metadata-${metadataName}`}
                    onClick={() =>
                      setSortBy(`${METADATA_SORT_PREFIX}${metadataName}`)
                    }
                    className="cursor-pointer"
                  >
                    {metadataName}
                  </DropdownMenuItem>
                ))}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="inline-flex h-9 items-center justify-center rounded-lg bg-muted p-1 text-muted-foreground">
          <Button
            variant={viewType === "list" ? "default" : "ghost"}
            size="sm"
            className={cn(
              "h-7 px-2 rounded-md",
              viewType === "list"
                ? "bg-background text-foreground shadow-sm hover:bg-background hover:text-foreground"
                : "hover:bg-transparent hover:text-foreground"
            )}
            onClick={() => setViewType("list")}
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            variant={viewType === "grid" ? "default" : "ghost"}
            size="sm"
            className={cn(
              "h-7 px-2 rounded-md",
              viewType === "grid"
                ? "bg-background text-foreground shadow-sm hover:bg-background hover:text-foreground"
                : "hover:bg-transparent hover:text-foreground"
            )}
            onClick={() => setViewType("grid")}
          >
            <Grid className="h-4 w-4" />
          </Button>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="outline"
              size="icon"
              disabled={
                documentsSynchronizing.length > 0 || !canUserUploadFiles()
              }
              onClick={handleSyncAll}
              className={cn(
                (documentsSynchronizing.length > 0 || !canUserUploadFiles()) &&
                  "opacity-50 cursor-not-allowed"
              )}
            >
              <RefreshCw
                className={cn(
                  "h-4 w-4",
                  documentsSynchronizing.length > 0 && "animate-spin"
                )}
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            {documentsSynchronizing.length > 0
              ? "Synkroniserer filer..."
              : !canUserUploadFiles()
                ? "Du har ikke rettigheder til at synkronisere filer"
                : "Synkroniser alle filer og mapper"}
          </TooltipContent>
        </Tooltip>
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild>
            <Button disabled={!canUserUploadFiles()}>
              <Plus className="mr-2 h-4 w-4" />
              Tilføj
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onCloseAutoFocus={addMenuClose.onCloseAutoFocus}
          >
            <DropdownMenuLabel>Tilføj til workspace</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => setIsCreateFolderDialogOpen(true)}
              >
                <FolderPlus className="mr-2 h-4 w-4" />
                Opret mappe
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setIsAddLinkDialogOpen(true)}>
                <Link2 className="mr-2 h-4 w-4" />
                Tilføj link
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onUploadFilesFromComputer}>
                <Upload className="mr-2 h-4 w-4" />
                Upload filer
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onUploadFolderFromComputer}>
                <FolderTree className="mr-2 h-4 w-4" />
                Upload mappe
              </DropdownMenuItem>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>OneDrive/SharePoint</DropdownMenuLabel>
            <DropdownMenuGroup>
              <DropdownMenuItem
                onClick={() => {
                  addMenuClose.schedule(() =>
                    openCloudLocationChooser(undefined, "file", "upload")
                  );
                }}
              >
                <img src={OneDriveIcon} className="size-4 mr-2" alt="ico" />
                <div className="flex flex-col justify-start items-start">
                  <p className="text-sm">
                    Upload filer fra OneDrive/SharePoint
                  </p>
                  <p className="text-xs text-border dark:text-primary-foreground/30 group-hover:text-secondary-foreground">
                    Vælg placering
                  </p>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => {
                  addMenuClose.schedule(() =>
                    openCloudLocationChooser(undefined, "folder", "upload")
                  );
                }}
              >
                <img src={OneDriveIcon} className="size-4 mr-2" alt="ico" />
                <div className="flex flex-col justify-start items-start">
                  <p className="text-sm">
                    Upload mappe fra OneDrive/SharePoint
                  </p>
                  <p className="text-xs text-border dark:text-primary-foreground/30 group-hover:text-secondary-foreground">
                    Vælg placering
                  </p>
                </div>
              </DropdownMenuItem>
              {acct && (
                <DropdownMenuItem
                  onClick={async () => {
                    await logoutLinkedAccount();
                  }}
                >
                  <img src={MSIcon} className="size-4 mr-2" alt="ico" />
                  Log ud {acct?.email || acct?.name || "Microsoft konto"}
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};

export default FilesTabToolbar;
